<template>
  <div class="row">
    <div class="col-sm-12 col-xl-6">
      <div class="form-card h-100 p-4">
        <h6 class="mb-4">Add Stock</h6>
        <form @submit.prevent="addNewStock">
          <!-- Platform Selection -->
          <div class="form-floating mb-3">
            <select
              class="form-select"
              id="select-platform"
              aria-label="Select Sector"
              name="platform"
              v-model="form.platform_id"
            >
              <option selected value="0">Select Platform</option>
              <option
                v-for="option in platforms"
                :key="option.platform_id"
                :value="option.platform_id"
              >
                {{ option.name }} - {{ option.username }}
              </option>
            </select>
            <label for="select-platform">Select Platform</label>
          </div>

          <!-- Stock Selection -->
          <div class="form-floating mb-3">
            <select
              class="form-select"
              id="select-stock"
              aria-label="Select Stock"
              name="stock_id"
              v-model="form.stock_id"
            >
              <option selected value="0">Select Stock</option>
              <option
                v-for="option in stock.filter(stock => stock.platform_id == form.platform_id)"
                :key="option.stock_id"
                :value="option.stock_id"
              >
                {{ option.name }}
              </option>
            </select>
            <label for="select-stock">Select Stock</label>
          </div>

          <!-- Form Fields for Stock Details -->
          <div class="form-floating mb-3">
            <input
              type="text"
              v-model="form.name"
              name="name"
              class="form-control"
              id="name"
              placeholder="Stock Name"
            />
            <label for="name">Name</label>
          </div>
          <div class="form-floating mb-3">
            <input
              type="text"
              v-model="form.code"
              name="code"
              class="form-control"
              id="symbol"
              placeholder="Symbol NSE/BSE"
            />
            <label for="symbol">Symbol</label>
          </div>

          <!-- Exchange Selection -->
          <div class="form-floating mb-3">
            <select
              class="form-select"
              id="select-exchange"
              aria-label="Select Exchange"
              name="exchange_id"
              v-model="form.exchange_id"
            >
              <option selected value="0">Select Exchange</option>
              <option
                v-for="option in exchanges"
                :key="option.exchange_id"
                :value="option.exchange_id"
              >
                {{ option.name }}
              </option>
            </select>
            <label for="select-exchange">Select Exchange</label>
          </div>

          <div class="form-floating mb-3">
            <input
              type="number"
              v-model="form.qty"
              name="qty"
              class="form-control"
              id="qty"
              placeholder="Quantity"
            />
            <label for="qty">Quantity</label>
          </div>

          <div class="form-floating mb-3">
            <input
              type="number"
              v-model="form.investment"
              name="investment"
              class="form-control"
              id="investment-amount"
              placeholder="Investment Amount"
            />
            <label for="investment-amount">Investment Amount</label>
          </div>

          <!-- Sector Selection -->
          <div class="form-floating mb-3">
            <select
              class="form-select"
              id="select-sector"
              aria-label="Select Sector"
              name="sector"
              v-model="form.sector_id"
            >
              <option selected value="0">Select Sector</option>
              <option
                v-for="option in sectors"
                :key="option.sector_id"
                :value="option.sector_id"
              >
                {{ option.name }}
              </option>
            </select>
            <label for="select-sector">Select Sector</label>
          </div>

          <!-- Date and Comments -->
          <div class="form-floating mb-3">
            <input
              type="date"
              v-model="form.perches_date"
              name="perches_date"
              class="form-control"
              id="perches-date"
              placeholder="Purchase Date"
            />
            <label for="perches-date">Purchase Date</label>
          </div>
          <div class="form-floating mb-3">
            <textarea
              class="form-control"
              v-model="form.comments"
              name="comments"
              placeholder="Leave a comment here"
              id="floatingTextarea"
              style="height: 150px"
            ></textarea>
            <label for="floatingTextarea">Comments</label>
          </div>
          <div class="form-floating mb-3">
              <input
                type="text"
                v-model="form.tags"
                name="tag"
                class="form-control"
                id="tag"
                placeholder="Enter tags, separated by commas"
              />
              <label for="tag">Tags (comma separated)</label>
          </div>

          <!-- Submit Button -->
          <div class="form-floating mb-3">
            <button
              :disabled="v$.form.$invalid || submitting"
              type="submit"
              class="btn btn-primary"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- File Upload and Control Section -->
    <div class="col-sm-12 col-xl-6">
      <div class="form-card h-100 p-4">
        <div class="mb-3">
          <label for="formFile" class="form-label">Upload File</label>
          <input class="form-control" type="file" id="formFile" @change="handleFileUpload" />
        </div>
        <div class="mb-3">
          <a href="/sample-stock-import.csv" download class="btn btn-outline-secondary btn-sm">
            Download Sample CSV
          </a>
        </div>
        <button type="submit" class="btn btn-primary" @click="uploadFile">Upload</button>
        <button
          type="button"
          class="btn btn-success m-2"
          @click="importData"
          :disabled="!dataRead"
        >
          Import
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import useVuelidate from "@vuelidate/core";
import { required, minValue } from "@vuelidate/validators";
import { useAlertStore } from "@/stores";
import { fetchWrapper } from "@/helpers";
import { gql } from "@apollo/client/core";
import { apolloClient } from "@/apollo-client";

// GraphQL Queries and Mutations
const PLATFORMS_OPTION_QUERY = gql`
  query Query {
    platforms {
      platform_id
      name
      username
    }
  }
`;

export default {
  name: "add-stock",
  setup() {
    return {
      v$: useVuelidate(),
      alertStore$: useAlertStore(),
    };
  },
  data() {
    return {
      form: {
        name: "",
        code: "",
        qty: "",
        investment: "",
        perches_date: "",
        comments: "",
        sector_id: "",
        platform_id: "",
        exchange_id: "",
        stock_id: "",
        tags: "",
      },
      sectors: [],
      platforms: [],
      exchanges: [],
      stock: [],
      file: null,
      fileUploaded: false,
      dataRead: false,
      submitting: false,
    };
  },
  validations() {
    return {
      form: {
        name: { required },
        code: { required },
        qty: { required, minValue: minValue(1) },
        investment: { required, minValue: minValue(1) },
        perches_date: { required },
        // minValue(1), not just required: the placeholder option's own
        // value is "0", not blank, so selecting "Select Platform" itself
        // has to fail validation too, not just leaving it untouched.
        platform_id: { required, minValue: minValue(1) },
      },
    };
  },
  methods: {
    // fetchWrapper.post rejects (rather than resolving with success:false)
    // on any non-2xx response, so its error can be a string message, a
    // plain HTTP status number, or an Error -- normalize whatever comes
    // back into something readable for the toast.
    describeError(error) {
      if (typeof error === "string") return error;
      if (error?.message) return error.message;
      return `Request failed (${error}).`;
    },
    resetForm() {
      this.form = {
        name: "",
        code: "",
        qty: "",
        investment: "",
        perches_date: "",
        comments: "",
        sector_id: "",
        platform_id: "",
        exchange_id: "",
        stock_id: "",
        tags: "",
      };
      this.v$.$reset();
    },
    async addNewStock() {
      if (this.submitting) return;
      this.submitting = true;
      try {
        // Split the comma-separated tags into an array and trim whitespace
        const tagsArray = this.form.tags
          .split(',')
          .map(tag => tag.trim())
          .filter(tag => tag); // Filter out any empty tags

        // Include the tags array in the payload if needed by your API
        const payload = {
          ...this.form,
          tags: tagsArray, // Add tags array to the payload
        };

        // Send the payload to the backend
        await fetchWrapper.post("api/stock/add-new", payload);
        this.alertStore$.success("New Stock added successfully.");
        this.resetForm();

        // The stock list reads through Apollo's cache on a separate page;
        // evict its cached result so the next visit fetches fresh instead
        // of silently showing pre-add data until a hard reload.
        apolloClient.cache.evict({ fieldName: 'stockListPlatformWise' });
        apolloClient.cache.gc();
      } catch (error) {
        this.alertStore$.error(this.describeError(error));
      } finally {
        this.submitting = false;
      }
    },
    // Capture file from the input change event
    handleFileUpload(event) {
      // Check if files are available in the event target
      if (event.target.files && event.target.files.length > 0) {
        this.file = event.target.files[0];
        this.dataRead = false;
      } else {
        console.error('No file selected');
        this.file = null;
      }
    },

    async uploadFile() {
      if (!this.file) {
        this.alertStore$.error("Please select a file first.");
        return;
      }

      const formData = new FormData();
      formData.append('file', this.file); // 'file' should match the field expected by your API
      try {
        await fetchWrapper.post('api/stock/import-new-stock-file', formData);
        this.dataRead = true; // Enable Import now that a file has actually been uploaded
        this.alertStore$.success("File uploaded. Click Import to process it.");
      } catch (error) {
        this.alertStore$.error(this.describeError(error));
      }
    },
    async importData() {
      try {
        // fetchWrapper already parses the JSON response body.
        const response = await fetchWrapper.post('api/stock/import-new-stock');
        if (response.success) {
          this.alertStore$.success(response.message);
        } else {
          this.alertStore$.error(response.message);
        }
        this.dataRead = false;
      } catch (error) {
        this.alertStore$.error(this.describeError(error));
      }
    },

  },
  mounted() {
    fetchWrapper.post("api/sector").then((response) => {
      this.sectors = response.data;
    });
    fetchWrapper.post("api/exchange").then((response) => {
      this.exchanges = response.data;
    });
  },
  watch: {
    "form.platform_id": function (newVal) {
      let canFetchNewStockList = this.stock.filter(
        (stock) => stock.platform_id == this.form.platform_id
      );
      if (canFetchNewStockList.length == 0) {
        fetchWrapper
          .post("api/stock", { platform_id: newVal })
          .then((response) => {
            this.stock = response.data;
          });
      }
    },
    "form.stock_id": function (newVal, oldVal) {
      if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
        // Prefill identity fields from the selected existing stock, but keep
        // whatever qty/investment/date/comments the user has already typed
        // for this purchase instead of overwriting the whole form.
        const row = this.stock.find((stock) => stock.stock_id == newVal);
        if (row) {
          this.form.name = row.name;
          this.form.code = row.code;
          this.form.sector_id = row.sector_id;
          this.form.exchange_id = row.exchange_id;
          this.form.tags = row.tags || "";
        }
      }
    },
  },
  apollo: {
    platforms: {
      query: PLATFORMS_OPTION_QUERY,
    },
  },
};
</script>

<style scoped>
.form-card {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: 12px;
}
</style>
